import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  CardMembership as PlanIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import shopService, { ShoppingPlan } from '../../services/shop.service';

const ShoppingPlans: React.FC = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<ShoppingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ShoppingPlan | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    features: 0,
    stores: 1,
    status: 'active' as 'active' | 'inactive',
  });

  const formatPrice = (price: number) => (price === 0 ? t('shop.free') : `${(price / 100).toFixed(0)} CVE/mes`);

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label={t('shop.active')} icon={<CheckCircle />} />
    ) : (
      <Chip size="small" color="default" label={t('shop.inactive')} icon={<InactiveIcon />} />
    );
  };

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await shopService.getPlans();

      if (response.success) {
        setPlans(response.data || []);
        if (response.needsSetup) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      }
    } catch (err: any) {
      console.error('Error fetching plans:', err);
      setError(t('shop.errorLoadingPlans'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleOpenEdit = (plan?: ShoppingPlan) => {
    if (plan) {
      setSelectedPlan(plan);
      setFormData({
        name: plan.name,
        price: plan.price,
        features: plan.features,
        stores: plan.stores,
        status: plan.status,
      });
    } else {
      setSelectedPlan(null);
      setFormData({
        name: '',
        price: 0,
        features: 0,
        stores: 1,
        status: 'active',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedPlan) {
        await shopService.updatePlan(selectedPlan.id, formData);
        setSuccessMessage(t('shop.planUpdated'));
      } else {
        await shopService.createPlan(formData);
        setSuccessMessage(t('shop.planCreated'));
      }
      setEditDialog(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (err: any) {
      console.error('Error saving plan:', err);
      setError(t('shop.errorSavingPlan'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan: ShoppingPlan) => {
    if (window.confirm(t('shop.confirmDeletePlan', { name: plan.name }))) {
      try {
        await shopService.deletePlan(plan.id);
        setSuccessMessage(t('shop.planDeleted'));
        fetchPlans();
      } catch (err: any) {
        console.error('Error deleting plan:', err);
        setError(t('shop.errorDeletingPlan'));
      }
    }
  };

  const handleSetupTables = async () => {
    setSettingUp(true);
    setError(null);
    try {
      const response = await shopService.setupTables();
      if (response.success) {
        setSuccessMessage(response.message);
        setNeedsSetup(false);
        fetchPlans();
      }
    } catch (err: any) {
      console.error('Error setting up tables:', err);
      setError(t('shop.errorSetup'));
    } finally {
      setSettingUp(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('shop.plansTitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPlans}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('shop.newPlan')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Setup Alert */}
      {!loading && needsSetup && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={settingUp ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
              onClick={handleSetupTables}
              disabled={settingUp}
            >
              {settingUp ? t('shop.settingUp') : t('shop.setupTables')}
            </Button>
          }
        >
          {t('shop.setupNeeded')}
        </Alert>
      )}

      {/* Plan Cards */}
      {!loading && plans.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {plans.map((plan) => (
            <Grid size={{ xs: 12, md: 4 }} key={plan.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" fontWeight={600}>
                      {plan.name}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleOpenEdit(plan)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(plan)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="h4" color="primary" sx={{ my: 2 }}>
                    {formatPrice(plan.price)}
                  </Typography>
                  <Typography variant="body2">{t('shop.featuresLabel')}: {plan.features}</Typography>
                  <Typography variant="body2">{t('shop.storesLabel')}: {plan.stores}</Typography>
                  {getStatusChip(plan.status)}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('shop.planHeader')}</TableCell>
                  <TableCell>{t('shop.priceHeader')}</TableCell>
                  <TableCell>{t('shop.featuresHeader')}</TableCell>
                  <TableCell>{t('shop.storesAllowed')}</TableCell>
                  <TableCell>{t('shop.statusHeader')}</TableCell>
                  <TableCell align="center">{t('shop.actionsHeader')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">{t('shop.noPlanFound')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  plans.map((plan) => (
                    <TableRow key={plan.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{plan.name}</Typography>
                      </TableCell>
                      <TableCell>{formatPrice(plan.price)}</TableCell>
                      <TableCell>{plan.features}</TableCell>
                      <TableCell>{plan.stores}</TableCell>
                      <TableCell>{getStatusChip(plan.status)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenEdit(plan)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(plan)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlanIcon />
          {selectedPlan ? t('shop.editPlan') : t('shop.newPlan')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('shop.planNameLabel')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.priceCentsLabel')}
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                helperText={`= ${formatPrice(formData.price)}`}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              >
                <MenuItem value="active">{t('shop.active')}</MenuItem>
                <MenuItem value="inactive">{t('shop.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.featuresCountLabel')}
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('shop.maxStoresLabel')}
                value={formData.stores}
                onChange={(e) => setFormData({ ...formData, stores: parseInt(e.target.value) || 1 })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? t('common.saving') : selectedPlan ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShoppingPlans;
